import { useState, useEffect } from "react";
import { PasswordGate } from "./components/PasswordGate";
import { AvailabilityGrid } from "./components/AvailabilityGrid";
import { SummaryBoard } from "./components/SummaryBoard";
import { GuestBook } from "./components/GuestBook";
import { useAvailabilityData } from "./hooks/useAvailabilityData";
import "./index.css";

type AppStage = "password" | "main";

export function App() {
  const [stage, setStage] = useState<AppStage>("password");
  const [userEmail, setUserEmail] = useState<string | null>(null);

  const {
    summary,
    participants,
    userResponse,
    loading,
    error,
    fetchSummary,
    fetchUserResponse,
    saveResponse,
    deleteResponse,
    clearUserResponse,
    toggleReaction,
    hasReacted,
    getReactionCount,
  } = useAvailabilityData();

  // Check for existing session on mount
  useEffect(() => {
    const checkSession = async () => {
      try {
        const storedEmail = localStorage.getItem("userEmail");
        if (storedEmail) {
          // Try to fetch user response to check if session is valid
          const res = await fetch(`/api/responses?email=${encodeURIComponent(storedEmail)}`);
          if (res.ok) {
            setUserEmail(storedEmail);
            // Always fetch user response to ensure state is properly set
            await fetchUserResponse(storedEmail);
            await fetchSummary();
            setStage("main");
          } else {
            // Session invalid, clear stored email
            localStorage.removeItem("userEmail");
          }
        }
      } catch {
        // No session, stay on password gate
      }
    };
    checkSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run on mount

  const handlePasswordSuccess = async (email: string) => {
    setUserEmail(email);
    localStorage.setItem("userEmail", email);

    // Always fetch user response to clear any previous user's data
    // This ensures userResponse is set to null if the new user has no response
    await fetchUserResponse(email);
    await fetchSummary();
    setStage("main");
  };

  const handleProfileSubmit = async (profileData: {
    name: string;
    email: string;
    phone?: string;
    note?: string;
  }) => {
    if (!userEmail) return;

    try {
      await saveResponse({
        ...profileData,
        availability: userResponse?.availability || [],
      });
      // Refresh user response after saving
      await fetchUserResponse(userEmail);
    } catch (err) {
      console.error("Failed to save profile:", err);
      // Error is already set by saveResponse, so it will be displayed
    }
  };

  const handleAvailabilitySave = async (availability: string[], name: string) => {
    if (!userEmail) return;

    try {
      await saveResponse({
        name,
        email: userEmail,
        phone: userResponse?.phone || undefined,
        note: userResponse?.note || undefined,
        availability,
      });
      // Refresh user response after saving
      await fetchUserResponse(userEmail);
    } catch (err) {
      console.error("Failed to save availability:", err);
    }
  };

  const handleSignOut = async () => {
    try {
      await fetch("/api/auth/verify", { method: "DELETE" });
    } catch {
      // Ignore errors
    }
    localStorage.removeItem("userEmail");
    setUserEmail(null);
    // Clear user response state when signing out
    clearUserResponse();
    setStage("password");
  };

  const handleDeleteResponse = async () => {
    if (!userEmail) return;
    if (!confirm("Are you sure you want to delete your response? This cannot be undone.")) {
      return;
    }

    try {
      await deleteResponse(userEmail);
      // Summary is automatically refreshed by deleteResponse hook
    } catch (err) {
      console.error("Failed to delete response:", err);
    }
  };

  return (
    <div className="app-container">
      {stage === "password" && <PasswordGate onSuccess={handlePasswordSuccess} />}

      {stage === "main" && userEmail && (
        <div className="main-content">
          <div className="header-bar">
            <div className="user-info">
              <span>Welcome{userResponse?.name ? `, ${userResponse.name}` : ""}!</span>
              <span className="user-email">({userEmail})</span>
            </div>
            <div className="header-actions">
              <button onClick={handleSignOut} className="planner-link-button">
                Sign Out
              </button>
              {userResponse && (
                <button onClick={handleDeleteResponse} className="planner-link-button delete">
                  Delete My Response
                </button>
              )}
            </div>
          </div>

          <div className="planner-box" style={{ marginBottom: "2rem" }}>
            <h2 className="planner-title">📅 Details</h2>
            <p className="planner-text">
              Add your details here
            </p>
          </div>

          <AvailabilityGrid
            userResponse={userResponse}
            availabilityCounts={summary?.availabilityCounts}
            onSave={handleAvailabilitySave}
          />

          {summary && <SummaryBoard summary={summary} participants={participants} />}

          {userResponse && (
            <GuestBook
              participants={participants}
              email={userEmail}
              existingResponse={userResponse}
              onSubmit={handleProfileSubmit}
              toggleReaction={toggleReaction}
              hasReacted={hasReacted}
              getReactionCount={getReactionCount}
            />
          )}

          {loading && <div className="loading-overlay">Loading...</div>}
          {error && <div className="planner-error">⚠️ {error}</div>}
        </div>
      )}
    </div>
  );
}

export default App;
